import React, { useState, useEffect } from "react";
import Header from "../../../components/Header/Header";
import Footer from "../../../components/Footer/Footer";
import DynamicTable from "../../../components/Table/MTable";
import ActionBar from "./MLActionBar/ActionBar";
import axiosInstance from "../../../utils/axiosConfig";
import { useSelector } from "react-redux";
import { Loader2 } from "lucide-react";

const MLocation = () => {
  const [locations, setLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [locationHeads, setLocationHeads] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const userRole = useSelector((state) => state.auth.role);

  const columns = [
    { key: "LocationID", label: "Location ID" },
    { key: "LocationName", label: "Location Name" },
    { key: "LocationEmail", label: "Location Email" },
    { key: "EnteredByUsername", label: "Created By" },
    { key: "LocationHeadUsername", label: "Location Head" }
  ];

  const fetchLocationHeads = async () => {
    try {
      const response = await axiosInstance.get(
        `/loc_head/location-head-users`
      );
      setLocationHeads(response.data);
    } catch (err) {
      console.error("Error fetching location heads:", err);
      setError(
        err.response?.data?.message || 
        "Failed to fetch location heads. Please try again later."
      );
    }
  };

  const fetchLocations = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axiosInstance.get(
        `/admin/mlocation/mlocations`
      );
      if (response.data) {
        setLocations(response.data);
        setFilteredLocations(response.data);
      } else {
        throw new Error('No data received from server');
      }
    } catch (err) {
      console.error("Error fetching locations:", err);
      setError(
        err.response?.data?.message || 
        "Failed to fetch locations. Please try again later."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (query) => {
    if (!query) {
      setFilteredLocations(locations);
      return;
    }

    const searchQuery = query.toLowerCase().trim();
    const filtered = locations.filter((location) => {
      // Safely check each property with optional chaining and fallback values
      const locationId = String(location?.LocationID || '').toLowerCase();
      const locationName = (location?.LocationName || '').toLowerCase();
      const locationEmail = (location?.LocationEmail || '').toLowerCase();
      const enteredBy = (location?.EnteredByUsername || '').toLowerCase();
      const locationHead = (location?.LocationHeadUsername || '').toLowerCase();

      // Check if any of the fields contain the search query
      return (
        locationId.includes(searchQuery) ||
        locationName.includes(searchQuery) ||
        locationEmail.includes(searchQuery) ||
        enteredBy.includes(searchQuery) ||
        locationHead.includes(searchQuery)
      );
    });

    setFilteredLocations(filtered);
  };

  useEffect(() => {
    const initializePage = async () => {
      await Promise.all([
        fetchLocations(),
        fetchLocationHeads()
      ]);
    };

    initializePage();
  }, []);

  const handleLocationHeadChange = (fieldName, value, formData, setFormData) => {
    if (fieldName === 'LHead') {
      const selectedUser = locationHeads.find(
        head => head.UserID === parseInt(value)
      );
      if (selectedUser) {
        setFormData({
          ...formData,
          LHead: value,
          LocationEmail: selectedUser.Email
        });
      }
    }
  };

  const handleRefresh = async () => {
    await fetchLocations();
  };

  if (error) {
    return (
      <div className="flex flex-col min-h-screen bg-gray-100">
        <Header />
        <main className="flex-grow p-6">
          <div className="max-w-6xl mx-auto">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="text-red-600 p-4 rounded-md bg-red-50 border border-red-200">
                <h3 className="text-lg font-semibold mb-2">Error</h3>
                <p>{error}</p>
                <button
                  onClick={handleRefresh}
                  className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-100">
      <Header />
      <main className="flex-grow p-4 sm:p-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-lg shadow-md">
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                  Location Master
                </h2>
                {isSubmitting && (
                  <div className="flex items-center text-blue-600">
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    <span>Processing...</span>
                  </div>
                )}
              </div>

              <ActionBar
                title="Location"
                fields={[
                  { 
                    name: "LocationName", 
                    label: "Location Name", 
                    type: "text",
                    required: true 
                  },
                  { 
                    name: "LHead", 
                    label: "Location Head", 
                    type: "select",
                    required: true,
                    options: locationHeads.map(head => ({
                      value: head.UserID.toString(),
                      label: head.Username,
                      email: head.Email
                    }))
                  },
                  { 
                    name: "LocationEmail", 
                    label: "Location Email", 
                    type: "text",
                    required: true,
                    readOnly: true 
                  },
                ]}
                apiEndpoint={`${process.env.REACT_APP_API_BASE_URL}/admin/mlocation/mlocations`}
                userRole="admin"
                data={filteredLocations}
                selectedRow={selectedRow}
                onRowSelect={setSelectedRow}
                onRefresh={handleRefresh}
                onSearch={handleSearch}
                rowKey="LocationID"
                onFieldChange={handleLocationHeadChange}
                onSubmitStart={() => setIsSubmitting(true)}
                onSubmitEnd={() => setIsSubmitting(false)}
              />

              {loading ? (
                <div className="flex justify-center items-center h-64">
                  <div className="flex flex-col items-center">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                    <p className="mt-2 text-gray-600">Loading locations...</p>
                  </div>
                </div>
              ) : (
                <div className="mt-6 overflow-x-auto">
                  <DynamicTable
                    columns={columns}
                    data={filteredLocations}
                    selectedRow={selectedRow}
                    onRowSelect={setSelectedRow}
                    rowKey="LocationID"
                    highlightColor="bg-green-100"
                    noDataMessage="No locations found."
                    className="min-w-full"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default MLocation;